import { AnimatePresence, m } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isNil } from 'lib/javascript';
import { sendEvent } from 'lib/gtm';

type Nutrition = number | null | undefined;

const AdditionalNutritionValues = ({
  nutritionalValue,
  displayedNutritionalValues,
}: {
  nutritionalValue: {
    fat: Nutrition;
    carb: Nutrition;
    polyunsaturated_fat: Nutrition;
    monounsaturated_fat: Nutrition;
    saturated_fat: Nutrition;
    trans_fat: Nutrition;
    dietary_fiber: Nutrition;
    sugar: Nutrition;
    cholesterol: Nutrition;
    potassium: Nutrition;
    vitamin_a: Nutrition;
    vitamin_c: Nutrition;
    calcium: Nutrition;
    iron: Nutrition;
  };
  displayedNutritionalValues: { [key: string]: boolean };
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenNutrition, setIsOpenNutrition] = useState(false);

  const transitionValues = {
    duration: 0.4,
    yoyo: Math.ceil(5 / 0.4),
    ease: 'easeOut',
  };

  const nutritionalValues = {
    left: [
      {
        key: 'polyunsaturated_fat',
        label: 'Polyunsaturated',
        unit: 'g',
        format: false,
      },
      {
        key: 'monounsaturated_fat',
        label: 'Monounsaturated',
        unit: 'g',
        format: false,
      },
      {
        key: 'saturated_fat',
        label: 'Saturated',
        unit: 'g',
        format: false,
      },
      {
        key: 'trans_fat',
        label: 'Trans Fat',
        unit: 'g',
        format: false,
      },
      {
        key: 'dietary_fiber',
        label: 'Dietary Fiber',
        unit: 'g',
        format: false,
      },
      {
        key: 'sugar',
        label: 'Sugar',
        unit: 'g',
        format: false,
      },
    ],
    right: [
      {
        key: 'cholesterol',
        label: 'Cholesterol',
        unit: 'mg',
        format: false,
      },
      {
        key: 'potassium',
        label: 'Potassium',
        unit: 'mg',
        format: false,
      },
      {
        key: 'vitamin_a',
        label: 'Vitamin A',
        unit: 'mcg',
        format: true,
      },
      {
        key: 'vitamin_c',
        label: 'Vitamin C',
        unit: 'mcg',
        format: true,
      },
      {
        key: 'calcium',
        label: 'Calcium',
        unit: 'mg',
        format: false,
      },
      {
        key: 'iron',
        label: 'Iron',
        unit: 'mg',
        format: false,
      },
    ],
  };

  useEffect(() => {
    if (isOpen) {
      sendEvent('view_nutrition_details', {
        content_type: 'button',
        content_id: 'more_nutrition_facts',
      });
    }
  }, [isOpen]);

  const isShowLeftSection = useMemo(
    () =>
      nutritionalValues.left.some((value) => {
        const val = nutritionalValue[value.key];
        return !isNil(val) && displayedNutritionalValues[value.key];
      }),
    [
      displayedNutritionalValues,
      nutritionalValue,
      nutritionalValues.left,
    ],
  );

  const isShowRightSection = useMemo(
    () =>
      nutritionalValues.right.some((value) => {
        const val = nutritionalValue[value.key];
        return !isNil(val) && displayedNutritionalValues[value.key];
      }),
    [
      displayedNutritionalValues,
      nutritionalValue,
      nutritionalValues.right,
    ],
  );

  const showMoreNutritionalSection =
    isShowLeftSection || isShowRightSection;

  if (!showMoreNutritionalSection) return null;

  const formatNutritionValue = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  };

  return (
    <div
      className={`tw-absolute -tw-top-12 md:-tw-top-14 tw-left-0 sm:tw-left-4 md:tw-left-8 tw-transition-transform tw-duration-300 ${
        isOpen
          ? '-tw-translate-y-[calc(100%-30px)] md:-tw-translate-y-[calc(100%-35px)]'
          : '-tw-translate-y-[2px] sm:-tw-translate-y-px md:sm:-tw-translate-y-0'
      }`}
    >
      <m.button
        onClick={() => {
          if (!isOpenNutrition) {
            setIsOpenNutrition(true);
          }
          setIsOpen((prev) => !prev);
        }}
        className="tw-cursor-pointer tw-inline-flex tw-border-opacity-10 tw-bg-white tw-border-b-0 tw-rounded-t-2xl tw-px-6 tw-py-3 tw-items-center tw-border tw-border-solid tw-border-gray-600"
        transition={
          isOpen || isOpenNutrition ? undefined : transitionValues
        }
        animate={
          isOpen || isOpenNutrition ? {} : { y: ['-3%', '10%'] }
        }
      >
        <span className="tw-font-bold tw-uppercase tw-text-xl md:tw-text-2xl tw-text-[#333]">
          More facts
        </span>{' '}
        <span
          className={`fas ml-3 ${
            isOpen ? 'fa-chevron-down' : 'fa-chevron-up'
          }`}
        ></span>
      </m.button>
      <div
        className={`tw-bg-white tw-border/40 tw-p-8 tw-border-b-0 tw-min-w-[94vw] sm:tw-min-w-[73vw] md:tw-min-w-[56rem] md:tw-max-w-4xl tw-rounded-tr-2xl tw-max-h-[calc(100vh-220px)] md:tw-max-h-[600px] tw-overflow-y-auto tw-border -tw-mt-[2px] ${
          isOpen ? 'tw-block' : 'tw-hidden'
        }`}
      >
        <div
          className={`tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-0 ${
            isShowRightSection && isShowLeftSection
              ? 'md:tw-grid-cols-2'
              : 'md:tw-grid-cols-1'
          }`}
        >
          {isShowLeftSection && (
            <div className="tw-flex tw-flex-col tw-w-full">
              <table className="table tw-w-full [&_td]:tw-border-white-800">
                <tbody>
                  <tr className="tw-border-y-[3px] tw-border-white-800">
                    <td className="tw-py-1.5 tw-px-2 tw-border-0 tw-font-bold tw-text-white-800 tw-text-xl md:tw-text-2xl">
                      Total Fat
                    </td>
                    <td className="tw-py-1.5 tw-px-2 tw-border-0 tw-font-bold tw-text-white-800 tw-text-xl md:tw-text-2xl tw-text-right">
                      {formatNutritionValue(
                        nutritionalValue?.fat ?? 0,
                      )}
                      g
                    </td>
                  </tr>
                  {nutritionalValues.left
                    .filter(
                      (nutrition) =>
                        !['dietary_fiber', 'sugar'].includes(
                          nutrition.key,
                        ) &&
                        displayedNutritionalValues[nutrition.key],
                    )
                    .map((nutrition) => (
                      <tr key={nutrition.key}>
                        <td className="tw-py-0 tw-pl-8">
                          {nutrition.label}
                        </td>
                        <td className="tw-py-0 tw-px-2 text-right">
                          {formatNutritionValue(
                            nutritionalValue?.[nutrition.key] ?? 0,
                          )}
                          {nutrition.unit}
                        </td>
                      </tr>
                    ))}
                  <tr className="tw-border-y-[3px] tw-border-white-800">
                    <td className="tw-py-1.5 tw-px-2 tw-border-0 tw-font-bold tw-text-white-800 tw-text-xl md:tw-text-2xl">
                      Total Carbs
                    </td>
                    <td className="tw-py-1.5 tw-px-2 tw-border-0 tw-font-bold tw-text-white-800 tw-text-xl md:tw-text-2xl tw-text-right">
                      {formatNutritionValue(
                        nutritionalValue?.carb ?? 0,
                      )}
                      g
                    </td>
                  </tr>
                  {nutritionalValues.left
                    .filter(
                      (nutrition) =>
                        ['dietary_fiber', 'sugar'].includes(
                          nutrition.key,
                        ) &&
                        displayedNutritionalValues[nutrition.key],
                    )
                    .map((nutrition) => (
                      <tr key={nutrition.key}>
                        <td className="tw-py-0 tw-pl-8">
                          {nutrition.label}
                        </td>
                        <td className="tw-py-0 tw-px-2 text-right">
                          {formatNutritionValue(
                            nutritionalValue?.[nutrition.key] ?? 0,
                          )}
                          {nutrition.unit}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {isShowRightSection && (
            <div className="tw-flex tw-flex-col tw-w-full">
              <table className="table tw-w-full [&_td]:tw-border-white-800 pl-8">
                <tbody>
                  <div className="tw-border-y-[3px] tw-border-white-800">
                    {nutritionalValues.right
                      .filter(
                        (nutrition) =>
                          displayedNutritionalValues[nutrition.key],
                      )
                      .map((nutrition) => (
                        <tr key={nutrition.key}>
                          <td className="tw-py-0 tw-pl-8 tw-w-full">
                            {nutrition.label}
                          </td>
                          <td className="tw-py-0 tw-px-2 text-right">
                            {formatNutritionValue(
                              nutritionalValue?.[nutrition.key] ?? 0,
                            )}
                            {nutrition.unit}
                          </td>
                        </tr>
                      ))}
                  </div>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdditionalNutritionValues;
